/**
 * Auth endpoints — proxied by cargo-server to the main CarWhats server.
 * Body shapes mirror Flutter `core/services/auth_service.dart` exactly:
 *   send  → { phoneNumber, method, language, appId }
 *   verify → { phoneNumber, code, language }
 * Older versions of the backend reject requests missing `appId`/`method`
 * with HTTP 500, so dropping them is what produced the "Internal server
 * error" we saw in the web client before this fix.
 */
import { api } from './client';

export interface SessionUser {
  id: string;
  phoneNumber: string;
  fullName?: string;
  avatarUrl?: string;
  isVerified?: boolean;
}

export interface VerifyCodeResponse {
  // Flutter response wrapper is `{ success, data: { token|accessToken,
  // refreshToken, user } }`. The cargo-server proxy passes this through
  // verbatim, so we accept both casings and pick the first non-null.
  token?: string;
  access_token?: string;
  accessToken?: string;
  refresh_token?: string;
  refreshToken?: string;
  user: SessionUser;
}

const APP_ID = 'carwhats';

export const authApi = {
  /** Request a phone OTP. The backend SMSes the code or pushes via WhatsApp. */
  async sendVerificationCode(
    phoneNumber: string,
    opts: { method?: 'auto' | 'sms' | 'whatsapp'; language?: string } = {}
  ): Promise<void> {
    await api.post('/api/auth/send-verification-code', {
      phoneNumber,
      method: opts.method || 'auto',
      language: opts.language || 'ar',
      appId: APP_ID,
    });
  },

  /** Verify the OTP and exchange it for a JWT pair. */
  async verifyCode(phoneNumber: string, code: string, language = 'ar'): Promise<VerifyCodeResponse> {
    const res = await api.post('/api/auth/verify-code', { phoneNumber, code, language });
    const payload = res.data?.data ?? res.data;
    return payload as VerifyCodeResponse;
  },

  /** Returns the currently logged-in user. */
  async me(): Promise<SessionUser> {
    const res = await api.get('/api/auth/me');
    return (res.data?.data ?? res.data?.user ?? res.data) as SessionUser;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // server-side logout failure is non-fatal — we still clear local tokens
    }
  },
};

/** Pull the access token out of either casing the backend returns. */
export function pickAccessToken(r: VerifyCodeResponse): string | null {
  return r.access_token || r.accessToken || r.token || null;
}

/** Pull the refresh token out of either casing the backend returns. */
export function pickRefreshToken(r: VerifyCodeResponse): string | null {
  return r.refresh_token || r.refreshToken || null;
}
